import React, { useEffect } from "react";

import { TaskItem } from "./models";
import { Button, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import Icon from '@mdi/react';
import { mdiCheck } from "@mdi/js";
import { useTranslation } from "react-i18next";
import BorderLinearProgress from "../BorderLinearProgress";
import './TaskViewMenu.css';

import { useNavigate } from "react-router-dom";
import { useStorage } from "../../services/StorageProvider";
import { useApiService } from "../../services/ApiServiceProvider";
import { AudioCheckDefinition } from "./acoustic-tasks/generic";
import WhoCompletedThisSurveyView from "./questionnaires/generic/WhoCompletedThisSurveyView";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskViewMenu = ({tasks, onChange}: { tasks: TaskItem[], onChange: (tasks: TaskItem[]) => void }) => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [sessionCompleted, setSessionCompleted] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false); // State for dialog
  const audioCheckHasBeenCompleted = tasks.find(x => x.name === AudioCheckDefinition.name)?.status === 'completed';
  const pendingTasks = tasks.filter(x => x.status !== 'completed');
  const completedTasks = tasks.filter(x => x.status === 'completed');
  const allTasksCompleted = tasks.length === completedTasks.length;
  const progress = (completedTasks.length + 1) / (tasks.length + 1) * 100;
  const { storage } = useStorage();
  const apiService = useApiService();
  const session: any = storage.session;
  const { t } = useTranslation();

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSubmit = (completedBy: string[]) => {
    completeSession(completedBy);
  };

  const completeSession = async (completedBy: string[]) => {
    console.log('All tasks completed');
    setLoading(true);

    try {
      const sessionPayload = {
        id: session.id,
        subjectId: session.subjectId,
        startedAt: session.startedAt,
        completedAt: new Date().toISOString(),
        status: 'completed',
        acousticTasks: [],
        completedBy: completedBy,
        site: session.site,
        isControlParticipant: session.isControlParticipant,
        assignedTasks: session.assignedTasks || []
      };
      await apiService.completeSession(sessionPayload);
      setSessionCompleted(true);
      navigate("/thank-you");
    } catch (error: Error | any) {
      console.error('Failed to complete session', error);
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (tasks.length > 0 && allTasksCompleted && !sessionCompleted) {
      // Open the dialog to ask who completed the survey
      setDialogOpen(true);
    }
  }, [tasks]);

  const handleGoToTask = (task: TaskItem) => {
    task.status = 'in-progress';
    navigate(`/data-collection/tasks/${task.name}`);
  };

  const isDisabled = (task: TaskItem) => {
    return task.status === 'completed'
      || (task.type === 'acoustic-task' && !(audioCheckHasBeenCompleted || task.name === AudioCheckDefinition.name));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
    <div style={{marginBottom: '1rem'}}>
      <BorderLinearProgress variant="determinate" value={progress} />
    </div>
    { allTasksCompleted ? (
      <>
      <Typography variant="h4" component="h1">{t('All tasks completed')}</Typography>
      <Typography variant="h6" component="h2">{t('Thank you for your participation!')}</Typography>
      <Button
        onClick={() => {
          if (sessionCompleted) return;
          setDialogOpen(true);
        }}
        style={{marginTop: '1rem'}}
        disabled={sessionCompleted}
      >
        {t("Finish")}
      </Button>
      </>
    ) : (
      <>
        <Typography variant="h4" component="h1" id="page-heading" tabIndex={-1} sx={{ mb: 3 }}>{t('Tasks')}</Typography>
        <Tabs
          style={{ marginBottom: '1rem' }}
          value={currentTab}
          onChange={(event, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
        >
          <Tab label={t('Pending')} value={0} />
          <Tab label={t('Completed')} value={1} />
        </Tabs>

        {(currentTab === 0 ? pendingTasks : completedTasks).map((task, index) => (
          <div key={index}>
            <button onClick={() => handleGoToTask(task)} className="task-button" disabled={isDisabled(task)}>
                <div style={{ textAlign: 'left' }}>
                  <Typography variant='h5' component='div'>{t(task.title, { ns: task.cohort })}</Typography>
                  <Typography fontWeight={100}>{t("Time")}: {task.estimatedCompletionTime}</Typography>
                </div>
                <div className={`task-icon ${task.type === 'acoustic-task' ? 'audio' : 'questionnaire'}`} >
                  {task.status === 'completed' && (
                  <div style={{position: 'absolute', right:'10px', bottom: '10px'}}>
                    <Icon className="task-completed-icon" path={mdiCheck} size={0.85} />
                  </div>
                  )}
                </div>
            </button>
          </div>
        ))}
      </>
    )}
      <WhoCompletedThisSurveyView
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
}

export default TaskViewMenu;

